import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  LayoutDashboard, Users, Key, CreditCard, Settings, Zap, TrendingUp, Activity,
  Menu, X, LogOut, CheckCircle2, XCircle, AlertTriangle as AlertTriangleIcon,
  Sparkles, Eye, ArrowLeft, DollarSign, Calculator, Pencil, Trash2, Plus, Package, Coins, MessageCircle, Save,
  Bell, Send, Shield, Ticket
} from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, ComposedChart, Line } from 'recharts';
import NotificationCenter from './NotificationCenter';
import { EditClientModal, EditLicenseModal, CreateLicenseModal, EditPackageModal, EditTopupModal, EditTransactionModal, EditCreditCostsModal } from './AdminCrudModals';
import ApiKeysSettings from './ApiKeysSettings';
import RedemptionAdminTab from './RedemptionAdminTab';
import Logo from './Logo';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#0055FF', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#EC4899'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-xs">
      <p className="text-[#A1A1AA] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">{p.name}: {typeof p.value === 'number' && p.value < 1 ? `$${p.value.toFixed(4)}` : p.value}</p>
      ))}
    </div>
  );
};

const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [clientDetailLoading, setClientDetailLoading] = useState(false);
  const [aiCosts, setAiCosts] = useState(null);
  const [aiCostsPeriod, setAiCostsPeriod] = useState(30);
  const [revenueData, setRevenueData] = useState(null);
  const [revenuePeriod, setRevenuePeriod] = useState(30);
  // CRUD modal states
  const [editClient, setEditClient] = useState(null);
  const [editLicense, setEditLicense] = useState(null);
  const [editTransaction, setEditTransaction] = useState(null);
  const [editPackage, setEditPackage] = useState(null);
  const [editTopup, setEditTopup] = useState(null);
  const [showCreateLicense, setShowCreateLicense] = useState(false);
  // 3.5.0 — Add credits to client modal state
  const [addCreditsClient, setAddCreditsClient] = useState(null);
  const [addCreditsAmount, setAddCreditsAmount] = useState(500);
  const [addCreditsType, setAddCreditsType] = useState('plan');
  const [addCreditsReason, setAddCreditsReason] = useState('');
  const [addCreditsLoading, setAddCreditsLoading] = useState(false);
  const [addCreditsResult, setAddCreditsResult] = useState(null);
  const [adminPackages, setAdminPackages] = useState({});
  const [adminTopupPackages, setAdminTopupPackages] = useState({});
  const [creditCosts, setCreditCosts] = useState(null);
  const [editCreditCosts, setEditCreditCosts] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLicenseConfirm, setDeleteLicenseConfirm] = useState(null);
  const [siteSettings, setSiteSettings] = useState({ whatsapp_number: '', whatsapp_message: '' });
  const [siteSettingsLoading, setSiteSettingsLoading] = useState(false);
  const [siteSettingsSaved, setSiteSettingsSaved] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [alertSettings, setAlertSettings] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: '', percent: 10, description: '', condition: '', active: true });
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [weeklyReportLoading, setWeeklyReportLoading] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [cronSettings, setCronSettings] = useState(null);
  const [cronStatus, setCronStatus] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStats, setEmailStats] = useState({});
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, clientsRes, transactionsRes, licensesRes, integrationsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { headers }),
        axios.get(`${API_URL}/api/admin/clients`, { headers }),
        axios.get(`${API_URL}/api/admin/transactions`, { headers }),
        axios.get(`${API_URL}/api/admin/licenses`, { headers }),
        axios.get(`${API_URL}/api/admin/config/integrations`, { headers }).catch(() => ({ data: null }))
      ]);

      setStats(statsRes.data);
      setClients(clientsRes.data);
      setTransactions(transactionsRes.data);
      setLicenses(licensesRes.data);
      setIntegrations(integrationsRes.data);
      // Fetch packages for create license modal
      try {
        const pkgRes = await axios.get(`${API_URL}/api/admin/packages`, { headers });
        setAdminPackages(pkgRes.data);
      } catch (e) { /* packages tab will re-fetch */ }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientDetail = async (clientId) => {
    setClientDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientDetail(res.data);
      setSelectedClient(clientId);
    } catch (err) {
      console.error('Client detail error:', err);
    } finally {
      setClientDetailLoading(false);
    }
  };

  const fetchAiCosts = async (days) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/ai-costs?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiCosts(res.data);
    } catch (err) {
      console.error('AI costs error:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'ai-costs' && user?.role === 'admin') {
      fetchAiCosts(aiCostsPeriod);
    }
  }, [activeTab, aiCostsPeriod]);

  const fetchRevenue = async (days) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/revenue?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRevenueData(res.data);
    } catch (err) {
      console.error('Revenue error:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'revenue' && user?.role === 'admin') {
      fetchRevenue(revenuePeriod);
    }
  }, [activeTab, revenuePeriod]);

  const fetchPackagesAdmin = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [pkgRes, topupRes, costsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/packages`, { headers }),
        axios.get(`${API_URL}/api/admin/topup-packages`, { headers }),
        axios.get(`${API_URL}/api/admin/credit-costs`, { headers }),
      ]);
      setAdminPackages(pkgRes.data);
      setAdminTopupPackages(topupRes.data);
      setCreditCosts(costsRes.data);
    } catch (err) {
      console.error('Packages fetch error:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'packages' && user?.role === 'admin') {
      fetchPackagesAdmin();
      fetchCoupons();
    }
  }, [activeTab]);

  const fetchSiteSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSiteSettings(res.data);
    } catch (err) {
      console.error('Site settings fetch error:', err);
    }
  };

  const saveSiteSettings = async () => {
    setSiteSettingsLoading(true);
    setSiteSettingsSaved(false);
    try {
      await axios.put(`${API_URL}/api/settings`, siteSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSiteSettingsSaved(true);
      setTimeout(() => setSiteSettingsSaved(false), 3000);
    } catch (err) {
      console.error('Save site settings error:', err);
    } finally {
      setSiteSettingsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'settings' && user?.role === 'admin') {
      fetchSiteSettings();
    }
  }, [activeTab]);

  const handleCrudRefresh = () => {
    fetchData();
    if (activeTab === 'packages') { fetchPackagesAdmin(); fetchCoupons(); }
  };

  const fetchCoupons = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API_URL}/api/admin/coupons`, { headers });
      setCoupons(res.data.coupons || []);
    } catch (err) { console.error('Fetch coupons error:', err); }
  };

  const createCoupon = async () => {
    if (!newCoupon.code.trim() || newCoupon.percent < 1) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/api/admin/coupons`, newCoupon, { headers });
      setNewCoupon({ code: '', percent: 10, description: '', condition: '', active: true });
      fetchCoupons();
    } catch (err) { alert(err.response?.data?.detail || 'Errore creazione coupon'); }
  };

  const toggleCoupon = async (code, currentActive) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const coupon = coupons.find(c => c.code === code);
      await axios.put(`${API_URL}/api/admin/coupons/${code}`, { ...coupon, active: !currentActive }, { headers });
      fetchCoupons();
    } catch (err) { alert(err.response?.data?.detail || 'Errore aggiornamento coupon'); }
  };

  const deleteCoupon = async (code) => {
    if (!confirm(`Eliminare il coupon ${code}?`)) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_URL}/api/admin/coupons/${code}`, { headers });
      fetchCoupons();
    } catch (err) { alert(err.response?.data?.detail || 'Errore eliminazione coupon'); }
  };

  // ── Alerts & Reports ──
  const fetchAlerts = async () => {
    setAlertsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [alertsRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/alerts/active`, { headers }),
        axios.get(`${API_URL}/api/admin/alerts/settings`, { headers })
      ]);
      setAlerts(alertsRes.data.alerts || []);
      setAlertSettings(settingsRes.data);
    } catch (err) { console.error('Fetch alerts error:', err); }
    finally { setAlertsLoading(false); }
  };

  const saveAlertSettings = async (newSettings) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API_URL}/api/admin/alerts/settings`, newSettings, { headers });
      setAlertSettings(newSettings);
      fetchAlerts();
    } catch (err) { console.error('Save alert settings error:', err); }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/api/admin/alerts/acknowledge/${alertId}`, {}, { headers });
      fetchAlerts();
    } catch (err) { console.error('Acknowledge alert error:', err); }
  };

  const fetchWeeklyReport = async () => {
    setWeeklyReportLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API_URL}/api/admin/reports/weekly-preview`, { headers });
      setWeeklyReport(res.data);
    } catch (err) { console.error('Fetch weekly report error:', err); }
    finally { setWeeklyReportLoading(false); }
  };

  const sendWeeklyReport = async () => {
    setSendingReport(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_URL}/api/admin/reports/send-weekly`, {}, { headers });
      alert('Report settimanale inviato a tutti i clienti attivi!');
      fetchCronStatus();
    } catch (err) { console.error('Send weekly report error:', err); alert('Errore invio report'); }
    finally { setSendingReport(false); }
  };

  const fetchCronSettings = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [settingsRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/reports/cron-settings`, { headers }),
        axios.get(`${API_URL}/api/admin/reports/cron-status`, { headers })
      ]);
      setCronSettings(settingsRes.data);
      setCronStatus(statusRes.data);
    } catch (err) { console.error('Fetch cron error:', err); }
  };

  const fetchCronStatus = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API_URL}/api/admin/reports/cron-status`, { headers });
      setCronStatus(res.data);
    } catch (err) { console.error('Fetch cron status error:', err); }
  };

  const saveCronSettings = async (newSettings) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.put(`${API_URL}/api/admin/reports/cron-settings`, newSettings, { headers });
      setCronSettings(newSettings);
      fetchCronStatus();
      alert(res.data.message || 'Impostazioni cron salvate');
    } catch (err) { console.error('Save cron error:', err); alert('Errore salvataggio cron'); }
  };

  const fetchEmailOnboarding = async () => {
    setEmailLogsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/onboarding-emails/logs?limit=15`, { headers }),
        axios.get(`${API_URL}/api/onboarding-emails/stats`, { headers })
      ]);
      setEmailLogs(logsRes.data.logs || []);
      setEmailStats(statsRes.data.stats || {});
    } catch (err) { console.error('Fetch email logs error:', err); }
    finally { setEmailLogsLoading(false); }
  };

  const testOnboardingEmail = async (emailType) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API_URL}/api/onboarding-emails/test-email?email_type=${emailType}&user_id=${user.id}`, {}, { headers });
      alert(res.data.success ? `Email tipo ${emailType} inviata a ${res.data.email}` : `Invio fallito: ${res.data.status}`);
      fetchEmailOnboarding();
    } catch (err) { alert('Errore invio test email'); }
  };

  useEffect(() => {
    if (activeTab === 'alerts' && user?.role === 'admin') {
      fetchAlerts();
      fetchWeeklyReport();
      fetchCronSettings();
      fetchEmailOnboarding();
    }
  }, [activeTab]);

  const handleDeactivateClient = async (clientId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(prev => prev.filter(c => c.id !== clientId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Deactivate error:', err);
    }
  };

  // 3.5.0 — Add credits to client wallet
  const handleAddCredits = async () => {
    if (!addCreditsClient || addCreditsAmount <= 0) return;
    setAddCreditsLoading(true);
    setAddCreditsResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/clients/${addCreditsClient.id}/credits`,
        {
          amount: parseInt(addCreditsAmount, 10),
          credit_type: addCreditsType,
          reason: addCreditsReason || `admin_grant_${addCreditsType}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAddCreditsResult({ success: true, ...res.data });
      // Refresh client list to show updated credit values
      handleCrudRefresh();
      // Auto-close after 1.6s
      setTimeout(() => {
        setAddCreditsClient(null);
        setAddCreditsResult(null);
        setAddCreditsAmount(500);
        setAddCreditsReason('');
      }, 1600);
    } catch (err) {
      console.error('Add credits error:', err);
      setAddCreditsResult({
        success: false,
        error: err.response?.data?.detail || err.message || 'Errore'
      });
    } finally {
      setAddCreditsLoading(false);
    }
  };

  const handleDeleteLicense = async (licenseId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/licenses/${licenseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLicenses(prev => prev.filter(l => l.id !== licenseId));
      setDeleteLicenseConfirm(null);
    } catch (err) {
      console.error('Delete license error:', err);
    }
  };

  const handleToggleGscManaged = async (license) => {
    const newValue = !license.gsc_managed_by_admin;
    try {
      const res = await axios.put(
        `${API_URL}/api/admin/licenses/${license.id}`,
        { gsc_managed_by_admin: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLicenses(prev => prev.map(l => l.id === license.id ? { ...l, ...res.data } : l));
    } catch (err) {
      console.error('Toggle GSC managed error:', err);
    }
  };

  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const navItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'revenue', label: 'Revenue', icon: Calculator },
    { id: 'clients', label: 'Clienti', icon: Users },
    { id: 'licenses', label: 'Licenze', icon: Key },
    { id: 'transactions', label: 'Transazioni', icon: CreditCard },
    { id: 'ai-costs', label: 'Costi AI', icon: DollarSign },
    { id: 'packages', label: 'Piani & Prezzi', icon: Package },
    { id: 'alerts', label: 'Alert & Report', icon: Bell },
    { id: 'redemption', label: 'Redemption', icon: Ticket },
    { id: 'settings', label: 'Impostazioni', icon: Settings },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Client Detail Panel
  const renderClientDetail = () => {
    if (!clientDetail) return null;
    const cd = clientDetail;
    return (
      <div className="space-y-6" data-testid="client-detail-panel">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => { setSelectedClient(null); setClientDetail(null); }} className="text-[#A1A1AA] hover:text-white" data-testid="back-to-clients-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h3 className="font-['Outfit'] font-medium text-lg">{cd.client?.company_name || cd.client?.email}</h3>
          <span className="text-xs text-[#A1A1AA]">{cd.client?.email}</span>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Chiamate API', value: cd.total_api_calls, color: '#0055FF' },
            { label: 'Token Totali', value: cd.total_tokens?.toLocaleString(), color: '#6366F1' },
            { label: 'Costo AI (USD)', value: `$${cd.total_ai_cost_usd}`, color: '#EF4444' },
            { label: 'Crediti Usati', value: `${cd.licenses?.reduce((s, l) => s + (l.api_calls_used || 0), 0)} / ${cd.licenses?.reduce((s, l) => s + (l.api_calls_limit || 0), 0)}`, color: '#F59E0B' },
            { label: 'Referral', value: `${cd.referrals?.converted || 0}/${cd.referrals?.total || 0}`, color: '#10B981' }
          ].map((s, i) => (
            <div key={i} className="bg-[#0a0a0a] rounded-xl p-3 border border-white/5">
              <span className="text-[9px] text-[#A1A1AA] uppercase tracking-wider block mb-1">{s.label}</span>
              <span className="text-lg font-['Outfit'] font-semibold" style={{ color: s.color }}>{s.value}</span>
            </div>
          ))}
        </div>

        {/* Licenses */}
        <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
          <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-3">Licenze</h4>
          {cd.licenses?.length > 0 ? (
            <div className="space-y-2">
              {cd.licenses.map((lic, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div>
                    <span className="text-sm font-medium">{lic.package_name}</span>
                    <span className="text-[10px] text-[#A1A1AA] ml-2 font-mono">{lic.license_key}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs">{lic.api_calls_used}/{lic.api_calls_limit}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${lic.status === 'active' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#EF4444]/20 text-[#EF4444]'}`}>{lic.status}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-[#A1A1AA]">Nessuna licenza</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Usage by Type */}
          <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
            <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-3">Costo per Tipo</h4>
            {cd.usage_by_type?.length > 0 ? (
              <div className="space-y-2">
                {cd.usage_by_type.map((ut, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      <span className="text-xs">{ut.type}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[10px]">
                      <span className="text-[#A1A1AA]">{ut.calls} calls</span>
                      <span className="text-[#A1A1AA]">{ut.tokens} tok</span>
                      <span className="font-medium text-[#EF4444]">${ut.cost_usd?.toFixed(4)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-[#A1A1AA]">Nessun dato</p>}
          </div>

          {/* Daily Usage Chart */}
          <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
            <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-3">Costo Giornaliero</h4>
            {cd.daily_usage?.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={cd.daily_usage.map(d => ({ ...d, date: d.date?.substring(5), cost_usd: Math.round(d.cost_usd * 10000) / 10000 }))}>
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="cost_usd" name="Costo $" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-[180px] flex items-center justify-center text-[#A1A1AA] text-xs">Nessun dato</div>}
          </div>
        </div>

        {/* Recent Calls */}
        {cd.recent_calls?.length > 0 && (
          <div className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
            <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-3">Ultime Chiamate AI</h4>
            <div className="overflow-x-auto">
              <table className="data-table text-xs">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Tipo</th>
                    <th>Token</th>
                    <th>Costo</th>
                  </tr>
                </thead>
                <tbody>
                  {cd.recent_calls.slice(0, 10).map((call, i) => (
                    <tr key={i}>
                      <td className="text-[#A1A1AA]">{call.timestamp?.substring(0, 16).replace('T', ' ')}</td>
                      <td>{call.type}</td>
                      <td>{call.total_tokens || '-'}</td>
                      <td className="text-[#EF4444]">{call.total_cost_usd ? `$${call.total_cost_usd.toFixed(4)}` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3">
            <Logo size={40} />
            <span className="font-['Outfit'] font-semibold text-lg">
              AEO Orchestra <span className="text-[#0055FF]">AI</span>
            </span>
          </Link>
        </div>

        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSidebarOpen(false); setSelectedClient(null); setClientDetail(null); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                activeTab === item.id ? 'bg-[#0055FF] text-white' : 'text-[#A1A1AA] hover:bg-white/5 hover:text-white'
              }`}
              data-testid={`admin-nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[#A1A1AA] hover:bg-white/5 hover:text-white transition-colors" data-testid="admin-logout-btn">
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="admin-content">
        <header className="sticky top-0 z-40 bg-[#111111] border-b border-white/10 px-6 py-4">
          <div className="flex items-center justify-between">
            <button className="lg:hidden p-2" onClick={() => setSidebarOpen(!sidebarOpen)}>
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <h1 className="font-['Outfit'] text-xl font-medium capitalize">{activeTab === 'ai-costs' ? 'Costi AI' : activeTab}</h1>
            <div className="flex items-center gap-4">
              <NotificationCenter />
              <span className="text-sm text-[#A1A1AA]">{user?.email}</span>
            </div>
          </div>
        </header>

        <main className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="stat-widget" data-testid="stat-clients">
                      <div className="flex items-center justify-between mb-4">
                        <Users className="w-8 h-8 text-[#0055FF]" />
                      </div>
                      <div className="text-3xl font-['Outfit'] font-semibold">{stats?.total_clients || 0}</div>
                      <div className="text-sm text-[#A1A1AA]">Clienti Totali</div>
                    </div>
                    <div className="stat-widget" data-testid="stat-licenses">
                      <div className="flex items-center justify-between mb-4">
                        <Key className="w-8 h-8 text-[#00E5FF]" />
                      </div>
                      <div className="text-3xl font-['Outfit'] font-semibold">{stats?.active_licenses || 0}</div>
                      <div className="text-sm text-[#A1A1AA]">Licenze Attive</div>
                    </div>
                    <div className="stat-widget" data-testid="stat-revenue">
                      <div className="flex items-center justify-between mb-4">
                        <TrendingUp className="w-8 h-8 text-[#10B981]" />
                      </div>
                      <div className="text-3xl font-['Outfit'] font-semibold">{formatCurrency(stats?.monthly_revenue || 0)}</div>
                      <div className="text-sm text-[#A1A1AA]">Revenue Mensile</div>
                    </div>
                    <div className="stat-widget" data-testid="stat-api">
                      <div className="flex items-center justify-between mb-4">
                        <Activity className="w-8 h-8 text-[#F59E0B]" />
                      </div>
                      <div className="text-3xl font-['Outfit'] font-semibold">{stats?.api_calls_today || 0}</div>
                      <div className="text-sm text-[#A1A1AA]">API Calls Oggi</div>
                    </div>
                  </div>

                  <div className="bg-[#111111] border border-white/10 rounded-xl overflow-hidden">
                    <div className="p-6 border-b border-white/10">
                      <h3 className="font-['Outfit'] font-medium text-lg">Transazioni Recenti</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead><tr><th>ID</th><th>Email</th><th>Package</th><th>Amount</th><th>Status</th><th>Data</th></tr></thead>
                        <tbody>
                          {transactions.slice(0, 5).map((tx) => (
                            <tr key={tx.id}>
                              <td className="font-mono text-xs">{tx.session_id?.slice(0, 12)}...</td>
                              <td>{tx.email || 'N/A'}</td>
                              <td>{tx.package_id}</td>
                              <td>{formatCurrency(tx.amount)}</td>
                              <td><span className={`badge ${tx.payment_status === 'paid' ? 'badge-active' : tx.payment_status === 'pending' ? 'badge-pending' : 'badge-expired'}`}>{tx.payment_status}</span></td>
                              <td>{formatDate(tx.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Revenue Tab */}
              {activeTab === 'revenue' && (
                <div className="space-y-6" data-testid="revenue-tab">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Calculator className="w-6 h-6 text-[#10B981]" />
                      <h3 className="font-['Outfit'] font-medium text-lg">Revenue Calculator</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      {[7, 30, 90].map(d => (
                        <button key={d} onClick={() => setRevenuePeriod(d)} className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${revenuePeriod === d ? 'bg-[#10B981] text-white' : 'bg-white/5 text-[#A1A1AA] hover:text-white'}`} data-testid={`revenue-period-${d}`}>
                          {d}gg
                        </button>
                      ))}
                    </div>
                  </div>

                  {revenueData ? (
                    <>
                      {/* Main KPI Cards */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                          { label: 'Revenue', value: `€${revenueData.total_revenue_eur}`, color: '#10B981', sub: `${revenueData.total_transactions} transazioni` },
                          { label: 'Costo AI', value: `€${revenueData.total_ai_cost_eur}`, color: '#EF4444', sub: `${revenueData.total_calls} chiamate` },
                          { label: 'Margine Netto', value: `€${revenueData.net_margin_eur}`, color: revenueData.net_margin_eur >= 0 ? '#0055FF' : '#EF4444', sub: revenueData.margin_pct == null ? 'n/d' : `${revenueData.margin_pct}%` },
                          { label: 'MRR', value: `€${revenueData.mrr}`, color: '#F59E0B', sub: 'ricorrente mensile' }
                        ].map((s, i) => (
                          <div key={i} className="bg-[#111111] rounded-xl p-4 border border-white/5">
                            <span className="text-[9px] text-[#A1A1AA] uppercase tracking-wider block mb-1">{s.label}</span>
                            <span className="text-2xl font-['Outfit'] font-bold" style={{ color: s.color }}>{s.value}</span>
                            <span className="text-[9px] text-[#A1A1AA] block mt-0.5">{s.sub}</span>
                          </div>
                        ))}
                      </div>

                      {/* Revenue vs Cost Chart */}
                      <div className="bg-[#111111] rounded-xl p-5 border border-white/5">
                        <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-4">Revenue vs Costo AI (giornaliero)</h4>
                        {revenueData.daily?.length > 0 ? (
                          <ResponsiveContainer width="100%" height={260}>
                            <ComposedChart data={revenueData.daily.map(d => ({ ...d, date: d.date?.substring(5) }))}>
                              <defs>
                                <linearGradient id="gradRev" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                              <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                              <Tooltip content={<CustomTooltip />} />
                              <Area yAxisId="left" type="monotone" dataKey="revenue" name="Revenue €" stroke="#10B981" fill="url(#gradRev)" strokeWidth={2} />
                              <Line yAxisId="right" type="monotone" dataKey="ai_cost" name="Costo AI €" stroke="#EF4444" strokeWidth={2} dot={false} />
                              <Bar yAxisId="left" dataKey="margin" name="Margine €" fill="#0055FF" opacity={0.3} radius={[2, 2, 0, 0]} />
                            </ComposedChart>
                          </ResponsiveContainer>
                        ) : <div className="h-[260px] flex items-center justify-center text-[#A1A1AA] text-sm">Nessun dato nel periodo</div>}
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Revenue Breakdown */}
                        <div className="bg-[#111111] rounded-xl p-5 border border-white/5">
                          <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-4">Revenue per Tipo</h4>
                          {(() => {
                            const rbt = revenueData.revenue_by_type || {};
                            const pieData = Object.entries(rbt).filter(([,v]) => v > 0).map(([k, v]) => ({
                              name: k === 'one_time' ? 'Licenze' : k === 'subscription' ? 'Abbonamenti' : 'Top-up',
                              value: v
                            }));
                            return pieData.length > 0 ? (
                              <>
                                <ResponsiveContainer width="100%" height={160}>
                                  <PieChart>
                                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} innerRadius={40}>
                                      {pieData.map((_, i) => <Cell key={i} fill={['#10B981', '#0055FF', '#F59E0B'][i]} />)}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                                  </PieChart>
                                </ResponsiveContainer>
                                <div className="space-y-2 mt-3">
                                  <div className="flex justify-between text-xs"><span className="text-[#A1A1AA]">Licenze una tantum</span><span className="text-[#10B981] font-medium">€{rbt.one_time || 0}</span></div>
                                  <div className="flex justify-between text-xs"><span className="text-[#A1A1AA]">Abbonamenti</span><span className="text-[#0055FF] font-medium">€{rbt.subscription || 0}</span></div>
                                  <div className="flex justify-between text-xs"><span className="text-[#A1A1AA]">Top-up crediti</span><span className="text-[#F59E0B] font-medium">€{rbt.topup || 0}</span></div>
                                </div>
                              </>
                            ) : (
                              <div className="h-[160px] flex flex-col items-center justify-center text-[#A1A1AA]">
                                <p className="text-sm mb-2">Nessuna transazione nel periodo</p>
                                <p className="text-[10px]">Le revenue appariranno qui dopo i primi acquisti</p>
                              </div>
                            );
                          })()}
                        </div>

                        {/* Projections */}
                        <div className="bg-[#111111] rounded-xl p-5 border border-white/5">
                          <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-4">Proiezioni</h4>
                          <div className="space-y-4">
                            <div className="bg-[#10B981]/5 border border-[#10B981]/20 rounded-xl p-4">
                              <span className="text-[9px] text-[#10B981] uppercase tracking-wider block mb-1">Proiezione Mensile</span>
                              <div className="grid grid-cols-3 gap-3 mt-2">
                                <div><span className="text-lg font-['Outfit'] font-bold text-[#10B981]">€{revenueData.projections?.monthly_revenue}</span><span className="text-[9px] text-[#A1A1AA] block">Revenue</span></div>
                                <div><span className="text-lg font-['Outfit'] font-bold text-[#EF4444]">€{revenueData.projections?.monthly_cost}</span><span className="text-[9px] text-[#A1A1AA] block">Costo AI</span></div>
                                <div><span className="text-lg font-['Outfit'] font-bold text-[#0055FF]">€{revenueData.projections?.monthly_margin}</span><span className="text-[9px] text-[#A1A1AA] block">Margine</span></div>
                              </div>
                            </div>
                            <div className="bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-xl p-4">
                              <span className="text-[9px] text-[#F59E0B] uppercase tracking-wider block mb-1">Proiezione Annuale</span>
                              <div className="grid grid-cols-2 gap-3 mt-2">
                                <div><span className="text-lg font-['Outfit'] font-bold text-[#F59E0B]">€{revenueData.projections?.annual_revenue}</span><span className="text-[9px] text-[#A1A1AA] block">Revenue</span></div>
                                <div><span className="text-lg font-['Outfit'] font-bold text-[#0055FF]">€{revenueData.projections?.annual_margin}</span><span className="text-[9px] text-[#A1A1AA] block">Margine</span></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Credit Economy */}
                      <div className="bg-[#111111] rounded-xl p-5 border border-white/5">
                        <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-4">Economia Crediti</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="text-center">
                            <span className="text-xl font-['Outfit'] font-bold text-white">{revenueData.total_credits_sold?.toLocaleString()}</span>
                            <span className="text-[9px] text-[#A1A1AA] block">Crediti venduti</span>
                          </div>
                          <div className="text-center">
                            <span className="text-xl font-['Outfit'] font-bold text-[#F59E0B]">{revenueData.total_credits_consumed?.toLocaleString()}</span>
                            <span className="text-[9px] text-[#A1A1AA] block">Crediti consumati</span>
                          </div>
                          <div className="text-center">
                            <span className="text-xl font-['Outfit'] font-bold text-[#10B981]">€{revenueData.revenue_per_credit}</span>
                            <span className="text-[9px] text-[#A1A1AA] block">Revenue/credito</span>
                          </div>
                          <div className="text-center">
                            <span className="text-xl font-['Outfit'] font-bold text-[#EF4444]">€{revenueData.cost_per_credit_eur}</span>
                            <span className="text-[9px] text-[#A1A1AA] block">Costo/credito</span>
                          </div>
                        </div>
                        {revenueData.margin_per_credit > 0 && (
                          <div className="mt-4 p-3 bg-[#10B981]/10 border border-[#10B981]/20 rounded-lg text-center">
                            <span className="text-xs text-[#10B981]">Margine per credito: <strong>€{revenueData.margin_per_credit}</strong> — ogni credito venduto ti genera profitto</span>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-64">
                      <div className="w-8 h-8 border-2 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              )}

              {/* Clients Tab */}
              {activeTab === 'clients' && (
                selectedClient && clientDetail ? renderClientDetail() : (
                  <div className="bg-[#111111] border border-white/10 rounded-xl overflow-hidden">
                    <div className="p-6 border-b border-white/10 flex items-center justify-between">
                      <h3 className="font-['Outfit'] font-medium text-lg">Tutti i Clienti</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Azienda</th><th>Email</th><th>Licenze</th><th>Crediti Wallet</th><th>API Calls</th><th>Costo AI</th><th>Spesa</th><th>Azioni</th>
                          </tr>
                        </thead>
                        <tbody>
                          {clients.length === 0 ? (
                            <tr><td colSpan={8} className="text-center py-8 text-[#A1A1AA]">Nessun cliente ancora</td></tr>
                          ) : (
                            clients.map((client) => (
                              <tr key={client.id}>
                                <td className="font-medium">{client.company_name || '-'}</td>
                                <td>{client.email}</td>
                                <td>{client.license_count || 0}</td>
                                <td className="font-mono text-xs">
                                  <div className="text-[#10B981] font-semibold">{(client.wallet_total || 0).toLocaleString('it-IT')}</div>
                                  <div className="text-[#A1A1AA] text-[10px]">
                                    P:{client.wallet_plan_credits || 0} · T:{client.wallet_topup_credits || 0}
                                  </div>
                                </td>
                                <td className="text-[#A1A1AA] font-mono text-xs">{client.credits_used || 0} / {client.credits_limit || 0}</td>
                                <td className="text-[#EF4444] font-mono text-xs">${(client.total_ai_cost_usd || 0).toFixed(4)}</td>
                                <td>{formatCurrency(client.total_spent || 0)}</td>
                                <td>
                                  <div className="flex items-center gap-1">
                                    <button onClick={() => fetchClientDetail(client.id)} className="text-[#0055FF] hover:text-[#3377FF] p-1" data-testid={`view-client-${client.id}`}>
                                      <Eye className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => setEditClient(client)} className="text-[#F59E0B] hover:text-[#FBBF24] p-1" data-testid={`edit-client-${client.id}`}>
                                      <Pencil className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => { setAddCreditsClient(client); setAddCreditsAmount(500); setAddCreditsType('plan'); setAddCreditsReason(''); setAddCreditsResult(null); }} className="text-[#10B981] hover:text-[#059669] p-1" title="Aggiungi crediti" data-testid={`add-credits-${client.id}`}>
                                      <Coins className="w-4 h-4" />
                                    </button>
                                    {deleteConfirm === client.id ? (
                                      <div className="flex items-center gap-1">
                                        <button onClick={() => handleDeactivateClient(client.id)} className="text-[#EF4444] text-sm font-medium hover:underline" data-testid={`confirm-delete-${client.id}`}>Conferma</button>
                                        <button onClick={() => setDeleteConfirm(null)} className="text-[#A1A1AA] text-sm">No</button>
                                      </div>
                                    ) : (
                                      <button onClick={() => setDeleteConfirm(client.id)} className="text-[#EF4444] hover:text-[#F87171] p-1" data-testid={`delete-client-${client.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              )}

              {/* Licenses Tab */}
              {activeTab === 'licenses' && (
                <div className="bg-[#111111] border border-white/10 rounded-xl overflow-hidden">
                  <div className="p-6 border-b border-white/10 flex items-center justify-between">
                    <h3 className="font-['Outfit'] font-medium text-lg">Tutte le Licenze</h3>
                    <Button onClick={() => setShowCreateLicense(true)} className="bg-[#10B981] hover:bg-[#059669] text-sm" data-testid="create-license-trigger">
                      <Plus className="w-4 h-4 mr-1" /> Crea Licenza
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead><tr><th>License Key</th><th>Package</th><th>Email</th><th>Dominio</th><th>API Calls</th><th>Scadenza</th><th>GSC</th><th>Status</th><th>Azioni</th></tr></thead>
                      <tbody>
                        {licenses.length === 0 ? (
                          <tr><td colSpan={9} className="text-center py-8 text-[#A1A1AA]">Nessuna licenza ancora</td></tr>
                        ) : (
                          licenses.map((license) => (
                            <tr key={license.id}>
                              <td className="font-mono text-xs text-[#00E5FF]">{license.license_key}</td>
                              <td>{license.package_name}</td>
                              <td>{license.email || 'N/A'}</td>
                              <td className="text-xs">{(() => {
                                // F2 25/07: i domini vivono in activated_domains (multi-sito 3.7.1);
                                // license.domain è il campo legacy pre-3.7.1 — solo fallback.
                                const doms = (license.activated_domains || []).filter(d => d.status === 'active').map(d => d.domain);
                                if (!doms.length && license.domain) doms.push(license.domain);
                                return doms.length
                                  ? <span className="text-[#10B981]" title={doms.join(', ')}>{doms.join(', ')}</span>
                                  : <span className="text-[#A1A1AA]">-</span>;
                              })()}</td>
                              <td>{license.api_calls_used} / {license.api_calls_limit}</td>
                              <td>{license.expires_at ? formatDate(license.expires_at) : 'Lifetime'}</td>
                              <td>
                                <button
                                  onClick={() => handleToggleGscManaged(license)}
                                  title={license.gsc_managed_by_admin ? 'Gestione centrale (admin connette GSC, cliente vede dati filtrati). Click per rendere self-service.' : 'Self-service (cliente connette il proprio GSC). Click per gestire centralmente.'}
                                  className={`text-xs px-2 py-1 rounded font-medium transition-colors ${license.gsc_managed_by_admin ? 'bg-[#0055FF]/20 text-[#3377FF] hover:bg-[#0055FF]/30' : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'}`}
                                  data-testid={`toggle-gsc-${license.id}`}
                                >
                                  {license.gsc_managed_by_admin ? 'Managed' : 'Self'}
                                </button>
                              </td>
                              <td><span className={`badge ${license.status === 'active' ? 'badge-active' : 'badge-expired'}`}>{license.status}</span></td>
                              <td>
                                <div className="flex items-center gap-1">
                                  <button onClick={() => setEditLicense(license)} className="text-[#F59E0B] hover:text-[#FBBF24] p-1" data-testid={`edit-license-${license.id}`}>
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  {deleteLicenseConfirm === license.id ? (
                                    <div className="flex items-center gap-1">
                                      <button onClick={() => handleDeleteLicense(license.id)} className="text-[#EF4444] text-sm font-medium hover:underline" data-testid={`confirm-delete-license-${license.id}`}>Conferma</button>
                                      <button onClick={() => setDeleteLicenseConfirm(null)} className="text-[#A1A1AA] text-sm">No</button>
                                    </div>
                                  ) : (
                                    <button onClick={() => setDeleteLicenseConfirm(license.id)} className="text-[#EF4444] hover:text-[#F87171] p-1" data-testid={`delete-license-${license.id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Transactions Tab */}
              {activeTab === 'transactions' && (
                <div className="bg-[#111111] border border-white/10 rounded-xl overflow-hidden">
                  <div className="p-6 border-b border-white/10"><h3 className="font-['Outfit'] font-medium text-lg">Storico Transazioni</h3></div>
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead><tr><th>Session ID</th><th>Email</th><th>Package</th><th>Amount</th><th>Currency</th><th>Status</th><th>Data</th><th>Azioni</th></tr></thead>
                      <tbody>
                        {transactions.length === 0 ? (
                          <tr><td colSpan={8} className="text-center py-8 text-[#A1A1AA]">Nessuna transazione ancora</td></tr>
                        ) : (
                          transactions.map((tx) => (
                            <tr key={tx.id}>
                              <td className="font-mono text-xs">{tx.session_id?.slice(0, 16)}...</td>
                              <td>{tx.email || 'N/A'}</td>
                              <td>{tx.package_id}</td>
                              <td>{formatCurrency(tx.amount)}</td>
                              <td className="uppercase">{tx.currency}</td>
                              <td><span className={`badge ${tx.payment_status === 'paid' ? 'badge-active' : tx.payment_status === 'pending' ? 'badge-pending' : 'badge-expired'}`}>{tx.payment_status}</span></td>
                              <td>{formatDate(tx.created_at)}</td>
                              <td>
                                <button onClick={() => setEditTransaction(tx)} className="text-[#F59E0B] hover:text-[#FBBF24] p-1" data-testid={`edit-tx-${tx.id || tx.session_id}`}>
                                  <Pencil className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* AI Costs Tab */}
              {activeTab === 'ai-costs' && (
                <div className="space-y-6" data-testid="ai-costs-tab">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-6 h-6 text-[#EF4444]" />
                      <h3 className="font-['Outfit'] font-medium text-lg">Costi AI</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      {[7, 30, 90].map(d => (
                        <button key={d} onClick={() => setAiCostsPeriod(d)} className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${aiCostsPeriod === d ? 'bg-[#0055FF] text-white' : 'bg-white/5 text-[#A1A1AA] hover:text-white'}`} data-testid={`ai-cost-period-${d}`}>
                          {d}gg
                        </button>
                      ))}
                    </div>
                  </div>

                  {aiCosts ? (
                    <>
                      {/* Cost Summary Cards */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                          { label: 'Costo Totale USD', value: `$${aiCosts.total_cost_usd}`, color: '#EF4444' },
                          { label: 'Costo Totale EUR', value: `€${aiCosts.total_cost_eur}`, color: '#F59E0B' },
                          { label: 'Chiamate Totali', value: aiCosts.total_calls, color: '#0055FF' },
                          { label: 'Costo Medio/Call', value: `$${aiCosts.avg_cost_per_call?.toFixed(6)}`, color: '#6366F1' }
                        ].map((s, i) => (
                          <div key={i} className="bg-[#111111] rounded-xl p-4 border border-white/5">
                            <span className="text-[9px] text-[#A1A1AA] uppercase tracking-wider block mb-1">{s.label}</span>
                            <span className="text-2xl font-['Outfit'] font-semibold" style={{ color: s.color }}>{typeof s.value === 'number' ? s.value.toLocaleString() : s.value}</span>
                          </div>
                        ))}
                      </div>

                      {/* Token Breakdown */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-[#111111] rounded-xl p-4 border border-white/5">
                          <span className="text-[10px] text-[#A1A1AA] uppercase block mb-1">Token Input</span>
                          <span className="text-xl font-['Outfit'] font-semibold text-[#0055FF]">{aiCosts.total_input_tokens?.toLocaleString()}</span>
                          <span className="text-[9px] text-[#A1A1AA] block">@ $0.15/1M</span>
                        </div>
                        <div className="bg-[#111111] rounded-xl p-4 border border-white/5">
                          <span className="text-[10px] text-[#A1A1AA] uppercase block mb-1">Token Output</span>
                          <span className="text-xl font-['Outfit'] font-semibold text-[#6366F1]">{aiCosts.total_output_tokens?.toLocaleString()}</span>
                          <span className="text-[9px] text-[#A1A1AA] block">@ $0.60/1M</span>
                        </div>
                        <div className="bg-[#111111] rounded-xl p-4 border border-white/5">
                          <span className="text-[10px] text-[#A1A1AA] uppercase block mb-1">Costo B2B API</span>
                          <span className="text-xl font-['Outfit'] font-semibold text-[#F59E0B]">${aiCosts.b2b_cost_usd}</span>
                          <span className="text-[9px] text-[#A1A1AA] block">{aiCosts.b2b_tokens?.toLocaleString()} token</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Daily Cost Chart */}
                        <div className="bg-[#111111] rounded-xl p-4 border border-white/5">
                          <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-4">Costo Giornaliero</h4>
                          {aiCosts.cost_by_day?.length > 0 ? (
                            <ResponsiveContainer width="100%" height={220}>
                              <AreaChart data={aiCosts.cost_by_day.map(d => ({ ...d, date: d.date?.substring(5) }))}>
                                <defs>
                                  <linearGradient id="gradCost" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#EF4444" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                                  </linearGradient>
                                </defs>
                                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Area type="monotone" dataKey="cost_usd" name="Costo $" stroke="#EF4444" fill="url(#gradCost)" strokeWidth={2} />
                              </AreaChart>
                            </ResponsiveContainer>
                          ) : <div className="h-[220px] flex items-center justify-center text-[#A1A1AA] text-sm">Nessun dato di costo</div>}
                        </div>

                        {/* Cost by Type */}
                        <div className="bg-[#111111] rounded-xl p-4 border border-white/5">
                          <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-4">Costo per Tipo Endpoint</h4>
                          {aiCosts.cost_by_type?.length > 0 ? (
                            <>
                              <ResponsiveContainer width="100%" height={150}>
                                <PieChart>
                                  <Pie data={aiCosts.cost_by_type} dataKey="cost_usd" nameKey="type" cx="50%" cy="50%" outerRadius={60} innerRadius={35}>
                                    {aiCosts.cost_by_type.map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                                  </Pie>
                                  <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                              </ResponsiveContainer>
                              <div className="space-y-1 mt-2">
                                {aiCosts.cost_by_type.map((item, i) => (
                                  <div key={i} className="flex items-center justify-between text-[10px] py-1">
                                    <div className="flex items-center gap-2">
                                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                                      <span className="text-[#A1A1AA]">{item.type}</span>
                                      {item.deterministic && (
                                        <span className="text-[8px] px-1.5 py-0.5 rounded bg-[#10B981]/15 text-[#10B981]" title="Operazione senza chiamata AI: il costo zero è legittimo">deterministica</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span>{item.calls} calls</span>
                                      <span className="text-[#EF4444] font-medium">${item.cost_usd}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : <div className="h-[150px] flex items-center justify-center text-[#A1A1AA] text-sm">Nessun dato</div>}
                        </div>
                      </div>

                      {/* Cost per Client */}
                      {aiCosts.cost_per_client?.length > 0 && (
                        <div className="bg-[#111111] rounded-xl p-4 border border-white/5">
                          <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-3">Costo per Cliente/Licenza</h4>
                          <div className="overflow-x-auto">
                            <table className="data-table text-xs">
                              <thead><tr><th>Licenza/Sorgente</th><th>Email</th><th>Package</th><th>Chiamate</th><th>Costo USD</th></tr></thead>
                              <tbody>
                                {aiCosts.cost_per_client.map((c, i) => (
                                  <tr key={i}>
                                    <td className="font-mono text-[#A1A1AA]">{c.license}</td>
                                    <td>{c.email || '-'}</td>
                                    <td>{c.package || '-'}</td>
                                    <td>{c.calls}</td>
                                    <td className="text-[#EF4444] font-medium">${c.cost_usd}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Pricing Info */}
                      <div className="bg-[#111111] rounded-xl p-4 border border-white/5">
                        <h4 className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-2">Modello & Pricing</h4>
                        <div className="flex items-center gap-6 text-xs">
                          <div><span className="text-[#A1A1AA]">Modello: </span><span className="font-mono text-[#00E5FF]">{aiCosts.model}</span></div>
                          <div><span className="text-[#A1A1AA]">Input: </span><span className="text-white">{aiCosts.pricing?.input_per_1m}/1M tok</span></div>
                          <div><span className="text-[#A1A1AA]">Output: </span><span className="text-white">{aiCosts.pricing?.output_per_1m}/1M tok</span></div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-64">
                      <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              )}


              {/* Packages Tab */}
              {activeTab === 'packages' && (
                <div className="space-y-6" data-testid="packages-tab">
                  {/* License Packages */}
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <Package className="w-6 h-6 text-[#0055FF]" />
                      <h3 className="font-['Outfit'] font-medium text-lg">Piani Licenza</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(adminPackages).map(([id, pkg]) => (
                        <div key={id} className="bg-[#111111] border border-white/10 rounded-2xl p-5 hover:border-[#0055FF]/30 transition-colors">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-['Outfit'] font-medium">{pkg.name}</h4>
                            <button onClick={() => setEditPackage({ id, pkg })} className="text-[#F59E0B] hover:text-[#FBBF24] p-1" data-testid={`edit-pkg-${id}`}>
                              <Pencil className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="text-2xl font-['Outfit'] font-bold text-[#0055FF] mb-1">{pkg.price}EUR</div>
                          <div className="text-[10px] text-[#A1A1AA] uppercase mb-3">{pkg.type || 'one_time'} {pkg.interval ? `/ ${pkg.interval}` : ''}</div>
                          <div className="space-y-1.5">
                            <div className="flex justify-between text-xs"><span className="text-[#A1A1AA]">Crediti</span><span>{pkg.initial_credits || pkg.monthly_credits || pkg.api_calls_limit || 0}</span></div>
                            <div className="flex justify-between text-xs"><span className="text-[#A1A1AA]">Limite API</span><span>{pkg.api_calls_limit || 0}</span></div>
                          </div>
                          {pkg.features?.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-white/5">
                              {pkg.features.slice(0, 4).map((f, i) => (
                                <p key={i} className="text-[10px] text-[#A1A1AA] py-0.5">{f}</p>
                              ))}
                              {pkg.features.length > 4 && <p className="text-[10px] text-[#555]">+{pkg.features.length - 4} altre...</p>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Top-up Packages */}
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <Coins className="w-6 h-6 text-[#F59E0B]" />
                      <h3 className="font-['Outfit'] font-medium text-lg">Pacchetti Top-up</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                      {Object.entries(adminTopupPackages).map(([id, pkg]) => (
                        <div key={id} className="bg-[#111111] border border-white/10 rounded-2xl p-4 hover:border-[#F59E0B]/30 transition-colors">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-sm">{pkg.name}</h4>
                            <button onClick={() => setEditTopup({ id, pkg })} className="text-[#F59E0B] hover:text-[#FBBF24] p-1" data-testid={`edit-topup-${id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="text-lg font-['Outfit'] font-bold text-[#F59E0B]">{pkg.price}EUR</div>
                          <div className="text-xs text-[#A1A1AA]">{pkg.credits?.toLocaleString()} crediti</div>
                          <div className="text-[10px] text-[#555] mt-1">{(pkg.price / pkg.credits * 1000).toFixed(2)}EUR/1K cr</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Credit Costs */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Zap className="w-6 h-6 text-[#10B981]" />
                        <h3 className="font-['Outfit'] font-medium text-lg">Costi Crediti per Operazione</h3>
                      </div>
                      <Button onClick={() => setEditCreditCosts(true)} className="bg-[#10B981] hover:bg-[#059669] text-sm" data-testid="edit-credit-costs-btn">
                        <Pencil className="w-4 h-4 mr-1" /> Modifica
                      </Button>
                    </div>
                    {creditCosts && (
                      <div className="bg-[#111111] border border-white/10 rounded-2xl p-5">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {Object.entries(creditCosts).map(([key, value]) => {
                            const labels = { seo_analysis: 'Analisi SEO', meta_generation: 'Meta Tag', content_generation_short: 'Contenuto Corto', content_generation_medium: 'Contenuto Medio', content_generation_long: 'Contenuto Lungo', local_seo: 'Local SEO', demo_analysis: 'Demo' };
                            return (
                              <div key={key} className="bg-white/5 rounded-xl p-3 text-center">
                                <span className="text-[10px] text-[#A1A1AA] block mb-1">{labels[key] || key}</span>
                                <span className="text-xl font-['Outfit'] font-bold text-[#10B981]">{value}</span>
                                <span className="text-[10px] text-[#A1A1AA] block">crediti</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Coupon Management */}
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <CreditCard className="w-6 h-6 text-[#8B5CF6]" />
                      <h3 className="font-['Outfit'] font-medium text-lg">Codici Sconto</h3>
                    </div>

                    {/* Create new coupon */}
                    <div className="bg-[#111111] border border-white/10 rounded-2xl p-5 mb-4">
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                        <div>
                          <label className="block text-xs text-[#A1A1AA] mb-1">Codice</label>
                          <input type="text" placeholder="SUMMER30" className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm uppercase"
                            data-testid="new-coupon-code"
                            value={newCoupon.code} onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value})} />
                        </div>
                        <div>
                          <label className="block text-xs text-[#A1A1AA] mb-1">Sconto %</label>
                          <input type="number" min="1" max="100" className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                            data-testid="new-coupon-percent"
                            value={newCoupon.percent} onChange={(e) => setNewCoupon({...newCoupon, percent: parseInt(e.target.value) || 0})} />
                        </div>
                        <div>
                          <label className="block text-xs text-[#A1A1AA] mb-1">Descrizione</label>
                          <input type="text" placeholder="Sconto estivo" className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                            value={newCoupon.description} onChange={(e) => setNewCoupon({...newCoupon, description: e.target.value})} />
                        </div>
                        <div>
                          <label className="block text-xs text-[#A1A1AA] mb-1">Condizione</label>
                          <select className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                            value={newCoupon.condition} onChange={(e) => setNewCoupon({...newCoupon, condition: e.target.value})}>
                            <option value="">Nessuna (tutti)</option>
                            <option value="trial_user">Solo utenti trial</option>
                          </select>
                        </div>
                        <Button className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm" data-testid="create-coupon-btn" onClick={createCoupon}>
                          <Plus className="w-4 h-4 mr-1" /> Crea
                        </Button>
                      </div>
                    </div>

                    {/* Coupons list */}
                    <div className="space-y-2">
                      {coupons.map((c) => (
                        <div key={c.code} className={`bg-[#111111] border rounded-xl p-4 flex items-center justify-between ${c.active ? 'border-white/10' : 'border-white/5 opacity-60'}`}
                          data-testid={`coupon-${c.code}`}>
                          <div className="flex items-center gap-4">
                            <span className="font-mono font-bold text-[#8B5CF6]">{c.code}</span>
                            <span className="text-xl font-bold text-white">-{c.percent}%</span>
                            <span className="text-xs text-[#A1A1AA]">{c.description}</span>
                            {c.condition === 'trial_user' && <span className="text-[10px] px-2 py-0.5 bg-[#F59E0B]/20 text-[#F59E0B] rounded-full">Solo trial</span>}
                            {c.source === 'system' && <span className="text-[10px] px-2 py-0.5 bg-[#0055FF]/20 text-[#0055FF] rounded-full">Sistema</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            {c.source !== 'system' && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => toggleCoupon(c.code, c.active)}
                                  className={c.active ? 'text-[#10B981]' : 'text-[#EF4444]'}>
                                  {c.active ? 'Attivo' : 'Disattivato'}
                                </Button>
                                <Button variant="ghost" size="sm" className="text-[#EF4444] hover:text-red-400" onClick={() => deleteCoupon(c.code)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {c.source === 'system' && <span className="text-xs text-[#10B981]">Sempre attivo</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}


              {/* Alerts & Weekly Report Tab */}
              {activeTab === 'alerts' && (
                <div className="space-y-6">
                  {/* Active Alerts */}
                  <div>
                    <h3 className="font-['Outfit'] font-medium text-lg mb-4 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-[#F59E0B]" /> Alert Attivi
                    </h3>
                    {alertsLoading ? (
                      <div className="text-center py-8 text-[#A1A1AA]">Caricamento alert...</div>
                    ) : alerts.length === 0 ? (
                      <div className="bg-[#111111] border border-[#10B981]/30 rounded-2xl p-6 text-center">
                        <CheckCircle2 className="w-10 h-10 text-[#10B981] mx-auto mb-2" />
                        <p className="text-[#10B981] font-medium">Nessun alert attivo</p>
                        <p className="text-[#A1A1AA] text-sm mt-1">Tutti i parametri sono nella norma.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {alerts.filter(a => !a.acknowledged).map((alert) => (
                          <div key={alert.id} data-testid={`alert-${alert.id}`} className={`bg-[#111111] border rounded-2xl p-5 ${
                            alert.severity === 'critical' ? 'border-[#EF4444]/50' :
                            alert.severity === 'warning' ? 'border-[#F59E0B]/50' : 'border-[#0055FF]/30'
                          }`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-3">
                                <div className={`p-2 rounded-lg ${
                                  alert.severity === 'critical' ? 'bg-[#EF4444]/20 text-[#EF4444]' :
                                  alert.severity === 'warning' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'bg-[#0055FF]/20 text-[#0055FF]'
                                }`}>
                                  <AlertTriangleIcon className="w-5 h-5" />
                                </div>
                                <div>
                                  <p className="font-medium text-white">{alert.title}</p>
                                  <p className="text-[#A1A1AA] text-sm mt-1">{alert.message}</p>
                                  <div className="flex gap-3 mt-2 text-xs">
                                    <span className={`px-2 py-0.5 rounded-full ${
                                      alert.severity === 'critical' ? 'bg-[#EF4444]/20 text-[#EF4444]' :
                                      alert.severity === 'warning' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'bg-[#0055FF]/20 text-[#0055FF]'
                                    }`}>{alert.severity}</span>
                                    <span className="text-[#A1A1AA]">{alert.type}</span>
                                  </div>
                                </div>
                              </div>
                              <Button variant="ghost" size="sm" className="text-[#A1A1AA] hover:text-white shrink-0"
                                onClick={() => acknowledgeAlert(alert.id)} data-testid={`ack-alert-${alert.id}`}>
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        {alerts.filter(a => a.acknowledged).length > 0 && (
                          <p className="text-[#A1A1AA] text-xs text-center mt-2">
                            {alerts.filter(a => a.acknowledged).length} alert riconosciuti (nascosti)
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Alert Settings */}
                  <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
                    <h3 className="font-['Outfit'] font-medium text-lg mb-4 flex items-center gap-2">
                      <Settings className="w-5 h-5 text-[#A1A1AA]" /> Configurazione Soglie
                    </h3>
                    {alertSettings ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-[#A1A1AA] mb-1">Budget giornaliero AI (EUR)</label>
                          <input type="number" step="0.5" className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white"
                            data-testid="alert-daily-budget"
                            value={alertSettings.daily_cost_budget_eur || 10}
                            onChange={(e) => setAlertSettings({...alertSettings, daily_cost_budget_eur: parseFloat(e.target.value)})} />
                        </div>
                        <div>
                          <label className="block text-sm text-[#A1A1AA] mb-1">Soglia margine minimo (%)</label>
                          <input type="number" step="5" className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white"
                            data-testid="alert-margin-threshold"
                            value={alertSettings.margin_threshold_pct || 50}
                            onChange={(e) => setAlertSettings({...alertSettings, margin_threshold_pct: parseFloat(e.target.value)})} />
                        </div>
                        <div>
                          <label className="block text-sm text-[#A1A1AA] mb-1">Soglia crediti bassi (per cliente)</label>
                          <input type="number" className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white"
                            data-testid="alert-low-credits"
                            value={alertSettings.low_credits_threshold || 100}
                            onChange={(e) => setAlertSettings({...alertSettings, low_credits_threshold: parseInt(e.target.value)})} />
                        </div>
                        <div className="flex items-end">
                          <Button className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                            data-testid="save-alert-settings"
                            onClick={() => saveAlertSettings(alertSettings)}>
                            <Save className="w-4 h-4 mr-1" /> Salva Soglie
                          </Button>
                        </div>
                      </div>
                    ) : <p className="text-[#A1A1AA]">Caricamento...</p>}
                  </div>

                  {/* Weekly Report Section */}
                  <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-['Outfit'] font-medium text-lg flex items-center gap-2">
                        <Send className="w-5 h-5 text-[#0055FF]" /> Report Settimanale
                      </h3>
                      <Button className="bg-[#0055FF] hover:bg-[#0044CC] text-white" disabled={sendingReport}
                        data-testid="send-weekly-report"
                        onClick={sendWeeklyReport}>
                        {sendingReport ? 'Invio in corso...' : <><Send className="w-4 h-4 mr-1" /> Invia Ora a Tutti</>}
                      </Button>
                    </div>
                    <p className="text-[#A1A1AA] text-sm mb-4">
                      Invia un riepilogo settimanale ai clienti con i dettagli del consumo crediti e operazioni AI.
                    </p>
                    {weeklyReportLoading ? (
                      <div className="text-center py-4 text-[#A1A1AA]">Caricamento anteprima...</div>
                    ) : weeklyReport ? (
                      <div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                          <div className="bg-[#0a0a0a] rounded-xl p-4 text-center">
                            <div className="text-2xl font-bold text-[#0055FF]">{weeklyReport.global?.active_clients || 0}</div>
                            <div className="text-xs text-[#A1A1AA]">Clienti Attivi</div>
                          </div>
                          <div className="bg-[#0a0a0a] rounded-xl p-4 text-center">
                            <div className="text-2xl font-bold text-[#10B981]">{weeklyReport.global?.total_calls || 0}</div>
                            <div className="text-xs text-[#A1A1AA]">Chiamate AI Settimana</div>
                          </div>
                          <div className="bg-[#0a0a0a] rounded-xl p-4 text-center">
                            <div className="text-2xl font-bold text-[#F59E0B]">{formatCurrency(weeklyReport.global?.total_revenue_eur || 0)}</div>
                            <div className="text-xs text-[#A1A1AA]">Revenue Settimana</div>
                          </div>
                          <div className="bg-[#0a0a0a] rounded-xl p-4 text-center">
                            <div className="text-2xl font-bold text-[#00E5FF]">{formatCurrency(weeklyReport.global?.margin_eur || 0)}</div>
                            <div className="text-xs text-[#A1A1AA]">Margine Settimana</div>
                          </div>
                        </div>
                        {weeklyReport.clients?.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-[#A1A1AA] mb-2">Top Clienti per Consumo ({weeklyReport.period?.from} - {weeklyReport.period?.to})</h4>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-white/10 text-[#A1A1AA]">
                                    <th className="text-left py-2 px-3">Email</th>
                                    <th className="text-right py-2 px-3">Operazioni</th>
                                    <th className="text-right py-2 px-3">Crediti Usati</th>
                                    <th className="text-right py-2 px-3">Rimanenti</th>
                                    <th className="text-left py-2 px-3">Agente Top</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {weeklyReport.clients.slice(0, 10).map((c, i) => (
                                    <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                                      <td className="py-2 px-3 text-white">{c.email}</td>
                                      <td className="py-2 px-3 text-right">{c.total_calls}</td>
                                      <td className="py-2 px-3 text-right font-medium text-[#00E5FF]">{c.total_credits_used}</td>
                                      <td className={`py-2 px-3 text-right font-medium ${c.remaining_credits < 100 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>{c.remaining_credits}</td>
                                      <td className="py-2 px-3 text-[#A1A1AA]">{c.top_agent}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : <p className="text-[#A1A1AA]">Nessun dato disponibile.</p>}
                  </div>

                  {/* Cron Scheduler Section */}
                  <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
                    <h3 className="font-['Outfit'] font-medium text-lg mb-4 flex items-center gap-2">
                      <Activity className="w-5 h-5 text-[#10B981]" /> Invio Automatico (Cron)
                    </h3>
                    <p className="text-[#A1A1AA] text-sm mb-4">
                      Configura l'invio automatico del report settimanale. Il sistema invia automaticamente le email ai clienti attivi nel giorno e orario configurati.
                    </p>
                    {cronSettings ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                          <div>
                            <label className="block text-sm text-[#A1A1AA] mb-1">Stato</label>
                            <button
                              data-testid="cron-toggle"
                              className={`w-full px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                                cronSettings.weekly_report_enabled
                                  ? 'bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/50'
                                  : 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30'
                              }`}
                              onClick={() => setCronSettings({...cronSettings, weekly_report_enabled: !cronSettings.weekly_report_enabled})}
                            >
                              {cronSettings.weekly_report_enabled ? 'Attivo' : 'Disattivato'}
                            </button>
                          </div>
                          <div>
                            <label className="block text-sm text-[#A1A1AA] mb-1">Giorno</label>
                            <select
                              data-testid="cron-day"
                              className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white"
                              value={cronSettings.day_of_week}
                              onChange={(e) => setCronSettings({...cronSettings, day_of_week: parseInt(e.target.value)})}
                            >
                              <option value={0}>Lunedi</option>
                              <option value={1}>Martedi</option>
                              <option value={2}>Mercoledi</option>
                              <option value={3}>Giovedi</option>
                              <option value={4}>Venerdi</option>
                              <option value={5}>Sabato</option>
                              <option value={6}>Domenica</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm text-[#A1A1AA] mb-1">Ora (UTC)</label>
                            <select
                              data-testid="cron-hour"
                              className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-white"
                              value={cronSettings.hour}
                              onChange={(e) => setCronSettings({...cronSettings, hour: parseInt(e.target.value)})}
                            >
                              {Array.from({length: 24}, (_, i) => (
                                <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <Button className="w-full bg-[#10B981] hover:bg-[#059669] text-white"
                              data-testid="save-cron-settings"
                              onClick={() => saveCronSettings(cronSettings)}>
                              <Save className="w-4 h-4 mr-1" /> Salva Cron
                            </Button>
                          </div>
                        </div>

                        {/* Cron Status */}
                        {cronStatus && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t border-white/5">
                            <div className="bg-[#0a0a0a] rounded-xl p-3">
                              <div className="text-xs text-[#A1A1AA] mb-1">Stato Scheduler</div>
                              <div className={`text-sm font-medium ${cronStatus.enabled ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                                {cronStatus.enabled ? `Attivo - ${cronStatus.schedule?.day} ore ${String(cronStatus.schedule?.hour || 0).padStart(2, '0')}:${String(cronStatus.schedule?.minute || 0).padStart(2, '0')} UTC` : 'Disattivato'}
                              </div>
                            </div>
                            <div className="bg-[#0a0a0a] rounded-xl p-3">
                              <div className="text-xs text-[#A1A1AA] mb-1">Ultimo Invio</div>
                              <div className="text-sm font-medium text-white">
                                {cronStatus.last_run?.timestamp
                                  ? <>{cronStatus.last_run.date} <span className={`text-xs ml-1 ${cronStatus.last_run.status === 'success' ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>({cronStatus.last_run.status}) - {cronStatus.last_run.clients_emailed} clienti</span></>
                                  : <span className="text-[#A1A1AA]">Mai eseguito</span>
                                }
                              </div>
                            </div>
                            <div className="bg-[#0a0a0a] rounded-xl p-3">
                              <div className="text-xs text-[#A1A1AA] mb-1">Prossimo Invio</div>
                              <div className="text-sm font-medium text-white">
                                {cronStatus.next_run
                                  ? cronStatus.next_run.substring(0, 16).replace('T', ' ') + ' UTC'
                                  : <span className="text-[#A1A1AA]">-</span>
                                }
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : <p className="text-[#A1A1AA]">Caricamento...</p>}
                  </div>

                  {/* Email Onboarding Section */}
                  <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-['Outfit'] font-medium text-lg flex items-center gap-2">
                        <Zap className="w-5 h-5 text-[#F59E0B]" /> Email Onboarding Automatiche
                      </h3>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="text-xs border-white/10" data-testid="test-email-0" onClick={() => testOnboardingEmail(0)}>
                          Test Email 0
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs border-white/10" data-testid="test-email-3" onClick={() => testOnboardingEmail(3)}>
                          Test Email 3
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs border-white/10" data-testid="test-email-7" onClick={() => testOnboardingEmail(7)}>
                          Test Email 7
                        </Button>
                        <Button variant="outline" size="sm" className="text-xs border-[#4338ca]/30 text-[#a5b4fc]" data-testid="test-email-14" onClick={() => testOnboardingEmail(14)}>
                          Test Email 14
                        </Button>
                      </div>
                    </div>
                    <p className="text-[#A1A1AA] text-sm mb-4">
                      4 email automatiche: Giorno 0 (benvenuto), Giorno 3 (utenti inattivi), Giorno 5 (trial in scadenza con sconto -20%), Giorno 7 (tips per utenti attivi). Cron giornaliero 10:00 UTC.
                    </p>

                    {/* Email Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      {['onboarding_0', 'onboarding_3', 'onboarding_14', 'onboarding_7'].map((type, idx) => {
                        const labels = ['Giorno 0 (Benvenuto)', 'Giorno 3 (Inattivi)', 'Giorno 5 (Trial Expiry)', 'Giorno 7 (Attivi)'];
                        const s = emailStats[type] || { sent: 0, failed: 0 };
                        return (
                          <div key={type} className="bg-[#0a0a0a] rounded-xl p-3 text-center">
                            <div className="text-xs text-[#A1A1AA] mb-1">{labels[idx]}</div>
                            <div className="flex justify-center gap-3">
                              <span className="text-[#10B981] font-bold">{s.sent || 0} <span className="text-xs font-normal">inviate</span></span>
                              {(s.failed || 0) > 0 && <span className="text-[#EF4444] font-bold">{s.failed} <span className="text-xs font-normal">fallite</span></span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Email Logs */}
                    {emailLogsLoading ? (
                      <p className="text-[#A1A1AA] text-center py-4">Caricamento log...</p>
                    ) : emailLogs.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-white/10 text-[#A1A1AA]">
                              <th className="text-left py-2 px-3">Data</th>
                              <th className="text-left py-2 px-3">Tipo</th>
                              <th className="text-left py-2 px-3">Destinatario</th>
                              <th className="text-left py-2 px-3">Stato</th>
                            </tr>
                          </thead>
                          <tbody>
                            {emailLogs.map((log, i) => (
                              <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                                <td className="py-2 px-3 text-white text-xs">{log.sent_at?.substring(0, 16).replace('T', ' ')}</td>
                                <td className="py-2 px-3">
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#3b82f6]/20 text-[#3b82f6]">
                                    {log.email_type?.replace('onboarding_', 'Giorno ')}
                                  </span>
                                </td>
                                <td className="py-2 px-3 text-[#A1A1AA] text-xs">{log.email_to}</td>
                                <td className="py-2 px-3">
                                  <span className={`text-xs font-medium ${log.status === 'sent' ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                                    {log.status === 'sent' ? 'Inviata' : 'Fallita'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-[#A1A1AA] text-center py-4 text-sm">Nessun log email disponibile.</p>
                    )}
                  </div>
                </div>
              )}


              {/* Settings Tab */}
                              {activeTab === 'redemption' && (
                  <RedemptionAdminTab token={token} />
                )}

                {activeTab === 'settings' && (
                <div className="space-y-6">
                  <h3 className="font-['Outfit'] font-medium text-lg">Stato Integrazioni</h3>
                  {integrations ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-[#111111] border border-white/10 rounded-2xl p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-[#6366F1]" /><span className="font-medium text-sm">Stripe</span></div>
                          {integrations.stripe.checkout_available ? <CheckCircle2 className="w-5 h-5 text-[#10B981]" /> : <XCircle className="w-5 h-5 text-[#EF4444]" />}
                        </div>
                        <div className="space-y-2 text-xs">
                          <div className="flex justify-between"><span className="text-[#A1A1AA]">Checkout</span><span className="text-[#10B981]">Attivo</span></div>
                          <div className="flex justify-between"><span className="text-[#A1A1AA]">Customer Portal</span><span className={integrations.stripe.portal_available ? 'text-[#10B981]' : 'text-[#F59E0B]'}>{integrations.stripe.portal_available ? 'Attivo' : 'Chiave reale richiesta'}</span></div>
                        </div>
                        {integrations.stripe.note && <div className="mt-3 p-2 rounded-lg bg-[#F59E0B]/5 border border-[#F59E0B]/20"><p className="text-[10px] text-[#F59E0B]">{integrations.stripe.note}</p></div>}
                      </div>
                      <div className="bg-[#111111] border border-white/10 rounded-2xl p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2"><svg className="w-5 h-5 text-[#00C4B4]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="m22 6-10 7L2 6"/></svg><span className="font-medium text-sm">Resend</span></div>
                          {integrations.resend.configured ? <CheckCircle2 className="w-5 h-5 text-[#10B981]" /> : <AlertTriangleIcon className="w-5 h-5 text-[#F59E0B]" />}
                        </div>
                        <div className="space-y-2 text-xs">
                          <div className="flex justify-between"><span className="text-[#A1A1AA]">Stato</span><span className={integrations.resend.configured ? 'text-[#10B981]' : 'text-[#F59E0B]'}>{integrations.resend.configured ? 'Configurato' : 'Non configurato'}</span></div>
                        </div>
                        {integrations.resend.note && <div className="mt-3 p-2 rounded-lg bg-[#F59E0B]/5 border border-[#F59E0B]/20"><p className="text-[10px] text-[#F59E0B]">{integrations.resend.note}</p></div>}
                      </div>
                      {/* AI Providers — multi-provider card (Anthropic + Google Gemini) */}
                      <div className="bg-[#111111] border border-white/10 rounded-2xl p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#F59E0B]" /><span className="font-medium text-sm">AI Providers</span></div>
                          {integrations.ai.configured ? <CheckCircle2 className="w-5 h-5 text-[#10B981]" /> : <XCircle className="w-5 h-5 text-[#EF4444]" />}
                        </div>
                        <div className="space-y-2 text-xs">
                          {(integrations.ai.providers || {}).anthropic ? (
                            <div className="flex justify-between items-start gap-2">
                              <div>
                                <div className="text-[#A1A1AA]">Anthropic Claude</div>
                                <div className="text-[10px] text-[#555] font-mono">{integrations.ai.providers.anthropic.models}</div>
                              </div>
                              <span className={integrations.ai.providers.anthropic.configured ? 'text-[#10B981]' : 'text-[#F59E0B]'}>{integrations.ai.providers.anthropic.configured ? 'Attivo' : 'Non configurato'}</span>
                            </div>
                          ) : null}
                          {(integrations.ai.providers || {}).google ? (
                            <div className="flex justify-between items-start gap-2 pt-2 border-t border-white/5">
                              <div>
                                <div className="text-[#A1A1AA]">Google Gemini</div>
                                <div className="text-[10px] text-[#555] font-mono">{integrations.ai.providers.google.models}</div>
                              </div>
                              <span className={integrations.ai.providers.google.configured ? 'text-[#10B981]' : 'text-[#F59E0B]'}>{integrations.ai.providers.google.configured ? 'Attivo' : 'Non configurato'}</span>
                            </div>
                          ) : (
                            // Fallback for older backend (pre 2026-05-09)
                            <>
                              <div className="flex justify-between"><span className="text-[#A1A1AA]">Provider</span><span>{integrations.ai.provider}</span></div>
                              <div className="flex justify-between"><span className="text-[#A1A1AA]">Modello</span><span className="font-mono">{integrations.ai.model}</span></div>
                            </>
                          )}
                        </div>
                      </div>
                      {/* Bing Webmaster Tools (Microsoft Azure) */}
                      <div className="bg-[#111111] border border-white/10 rounded-2xl p-5" data-testid="integration-bing">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-[#0078D4]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.35-4.35"/></svg>
                            <span className="font-medium text-sm">Bing Webmaster</span>
                          </div>
                          {integrations.bing?.oauth_ready ? <CheckCircle2 className="w-5 h-5 text-[#10B981]" /> : <XCircle className="w-5 h-5 text-[#A1A1AA]" />}
                        </div>
                        <div className="space-y-2 text-xs">
                          <div className="flex justify-between"><span className="text-[#A1A1AA]">Stato</span><span className={integrations.bing?.oauth_ready ? 'text-[#10B981]' : 'text-[#A1A1AA]'}>{integrations.bing?.oauth_ready ? 'Configurato' : 'Non configurato'}</span></div>
                          {integrations.bing?.oauth_ready && (
                            <>
                              <div className="flex justify-between"><span className="text-[#A1A1AA]">OAuth ready</span><span className="text-[#10B981]">✓</span></div>
                              <div className="flex justify-between"><span className="text-[#A1A1AA]">Customer connections</span><span className="font-mono">{integrations.bing.customer_connections}</span></div>
                            </>
                          )}
                          {!integrations.bing?.oauth_ready && (
                            <p className="text-[10px] text-[#555]">Configura le 3 chiavi Microsoft Azure qui sotto.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-[#111111] border border-white/10 rounded-xl p-6"><p className="text-[#A1A1AA]">Caricamento...</p></div>
                  )}

                  {/* API Keys Management */}
                  <ApiKeysSettings token={token} />

                  {/* WhatsApp Business Settings */}
                  <div className="bg-[#111111] border border-white/10 rounded-2xl p-6" data-testid="whatsapp-settings">
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-[#25D366]/10">
                          <MessageCircle className="w-5 h-5 text-[#25D366]" />
                        </div>
                        <div>
                          <h3 className="font-['Outfit'] font-medium text-lg">WhatsApp Business</h3>
                          <p className="text-xs text-[#A1A1AA]">Numero visibile nella pagina Contatti e CTA</p>
                        </div>
                      </div>
                      {siteSettingsSaved && (
                        <span className="flex items-center gap-1 text-[#10B981] text-xs">
                          <CheckCircle2 className="w-4 h-4" /> Salvato
                        </span>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-2">Numero WhatsApp (con prefisso internazionale)</label>
                        <input
                          type="text"
                          value={siteSettings.whatsapp_number}
                          onChange={(e) => setSiteSettings(prev => ({ ...prev, whatsapp_number: e.target.value }))}
                          className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-white/10 text-white text-sm focus:border-[#25D366] focus:outline-none transition-colors"
                          placeholder="+39 333 123 4567"
                          data-testid="whatsapp-number-input"
                        />
                        <p className="text-[10px] text-[#555] mt-1">Formato: +39XXXXXXXXXX (senza spazi per il link, con spazi per la visualizzazione)</p>
                      </div>

                      <div>
                        <label className="block text-sm text-[#A1A1AA] mb-2">Messaggio predefinito</label>
                        <textarea
                          value={siteSettings.whatsapp_message}
                          onChange={(e) => setSiteSettings(prev => ({ ...prev, whatsapp_message: e.target.value }))}
                          rows={2}
                          className="w-full px-4 py-3 rounded-xl bg-[#0a0a0a] border border-white/10 text-white text-sm focus:border-[#25D366] focus:outline-none transition-colors resize-none"
                          placeholder="Ciao! Vorrei informazioni su AEO Orchestra..."
                          data-testid="whatsapp-message-input"
                        />
                      </div>

                      <div className="flex items-center justify-between pt-2">
                        {siteSettings.whatsapp_number && (
                          <a
                            href={`https://wa.me/${siteSettings.whatsapp_number.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(siteSettings.whatsapp_message)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#25D366] hover:underline"
                            data-testid="whatsapp-preview-link"
                          >
                            Testa il link WhatsApp
                          </a>
                        )}
                        <Button
                          onClick={saveSiteSettings}
                          disabled={siteSettingsLoading}
                          className="bg-[#25D366] hover:bg-[#20BD5A] text-white text-sm"
                          data-testid="whatsapp-save-btn"
                        >
                          {siteSettingsLoading ? (
                            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-1" />
                              Salva WhatsApp
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)}></div>}

      {/* CRUD Modals */}
      {/* 3.5.0 — Add Credits modal */}
      {addCreditsClient && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget && !addCreditsLoading) setAddCreditsClient(null); }}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-['Outfit'] font-semibold flex items-center gap-2">
                <Coins className="w-5 h-5 text-[#10B981]" /> Aggiungi crediti
              </h3>
              <button onClick={() => !addCreditsLoading && setAddCreditsClient(null)} className="text-[#A1A1AA] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="text-xs text-[#A1A1AA] mb-4">
              Cliente: <span className="text-white font-medium">{addCreditsClient.company_name || addCreditsClient.email}</span><br />
              {addCreditsClient.email}
            </div>

            {addCreditsResult && addCreditsResult.success ? (
              <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg p-3 text-sm text-[#34D399]">
                ✓ Aggiunti <strong>{addCreditsResult.added}</strong> crediti ({addCreditsResult.credit_type}).<br />
                <span className="text-xs text-[#A1A1AA]">
                  Wallet ora: piano {addCreditsResult.wallet?.plan_credits} · top-up {addCreditsResult.wallet?.topup_credits} · totale {addCreditsResult.wallet?.total}
                </span>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <label className="block text-xs text-[#A1A1AA] mb-1">Quantità</label>
                  <input
                    type="number"
                    min="1"
                    value={addCreditsAmount}
                    onChange={e => setAddCreditsAmount(parseInt(e.target.value) || 0)}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
                    disabled={addCreditsLoading}
                    data-testid="add-credits-amount"
                  />
                </div>

                <div className="mb-3">
                  <label className="block text-xs text-[#A1A1AA] mb-1">Tipo</label>
                  <div className="flex gap-2">
                    <button onClick={() => setAddCreditsType('plan')} disabled={addCreditsLoading}
                      className={`flex-1 py-2 px-3 rounded-lg text-xs border transition-all ${addCreditsType === 'plan' ? 'bg-[#0055FF]/20 border-[#0055FF] text-white' : 'bg-black/40 border-white/10 text-[#A1A1AA] hover:border-white/20'}`}
                      data-testid="add-credits-type-plan">
                      Piano <span className="block text-[10px] opacity-70">consumati per primi</span>
                    </button>
                    <button onClick={() => setAddCreditsType('topup')} disabled={addCreditsLoading}
                      className={`flex-1 py-2 px-3 rounded-lg text-xs border transition-all ${addCreditsType === 'topup' ? 'bg-[#10B981]/20 border-[#10B981] text-white' : 'bg-black/40 border-white/10 text-[#A1A1AA] hover:border-white/20'}`}
                      data-testid="add-credits-type-topup">
                      Top-up <span className="block text-[10px] opacity-70">extra ricarica</span>
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-xs text-[#A1A1AA] mb-1">Motivazione (opzionale, solo audit log)</label>
                  <input
                    type="text"
                    value={addCreditsReason}
                    onChange={e => setAddCreditsReason(e.target.value)}
                    placeholder={`admin_grant_${addCreditsType}`}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
                    disabled={addCreditsLoading}
                    data-testid="add-credits-reason"
                  />
                </div>

                {addCreditsResult && !addCreditsResult.success && (
                  <div className="mb-3 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg p-2 text-xs text-[#FCA5A5]">
                    {addCreditsResult.error}
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button onClick={() => setAddCreditsClient(null)} variant="outline" className="border-white/10 text-xs" disabled={addCreditsLoading}>
                    Annulla
                  </Button>
                  <Button onClick={handleAddCredits} disabled={addCreditsLoading || addCreditsAmount <= 0}
                    className="bg-[#10B981] hover:bg-[#059669] text-white text-xs" data-testid="add-credits-confirm">
                    {addCreditsLoading ? 'Aggiunta...' : `Aggiungi +${addCreditsAmount}`}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <EditClientModal isOpen={!!editClient} onClose={() => setEditClient(null)} client={editClient} onSave={handleCrudRefresh} token={token} />
      <EditLicenseModal isOpen={!!editLicense} onClose={() => setEditLicense(null)} license={editLicense} onSave={handleCrudRefresh} token={token} />
      <CreateLicenseModal isOpen={showCreateLicense} onClose={() => setShowCreateLicense(false)} packages={adminPackages} onSave={handleCrudRefresh} token={token} />
      <EditPackageModal isOpen={!!editPackage} onClose={() => setEditPackage(null)} pkg={editPackage?.pkg} packageId={editPackage?.id} onSave={handleCrudRefresh} token={token} />
      <EditTopupModal isOpen={!!editTopup} onClose={() => setEditTopup(null)} pkg={editTopup?.pkg} packageId={editTopup?.id} onSave={handleCrudRefresh} token={token} />
      <EditTransactionModal isOpen={!!editTransaction} onClose={() => setEditTransaction(null)} transaction={editTransaction} onSave={handleCrudRefresh} token={token} />
      <EditCreditCostsModal isOpen={editCreditCosts} onClose={() => setEditCreditCosts(false)} costs={creditCosts} onSave={handleCrudRefresh} token={token} />
    </div>
  );
};

export default AdminDashboard;
